/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  dataFetcher, endpoints, baseUrl, getToken,
} from 'Api';
import UsersPanel from './UsersPanel';
import MembershipUpgradeModal from './MembershipUpgradeModal';
import styles from './index.module.css';

const formatMoney = (cents) => `$${Math.round((Number(cents) || 0) / 100).toLocaleString('en-US')}`;
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Applied promo/coupon (membership.discount) → "Free", "40% off", "$20 off".
const discountValue = (d) => {
  if (!d) return '';
  if (d.percent_off) return d.percent_off === 100 ? 'Free' : `${d.percent_off}% off`;
  if (d.amount_off) return `${formatMoney(d.amount_off)} off`;
  return '';
};
const discountDuration = (d) => {
  if (!d) return '';
  if (d.duration === 'forever') return 'forever';
  if (d.duration === 'once') return 'one-time';
  if (d.duration === 'repeating') return d.duration_in_months ? `for ${d.duration_in_months} mo` : 'recurring';
  return '';
};

// A creation discount ({type,value}) → short label. 100% off reads as FREE.
const fmtDisc = (d) => {
  if (!d || !d.value) return '';
  if (d.type === 'percentage') return Number(d.value) === 100 ? 'Included' : `${d.value}% off`;
  return `$${(Number(d.value) / 100).toFixed(2)} off`;
};

// Badge as the (rounded) price the manager pays for a user on this plan after the
// discount, e.g. 25% off a $639 plan → "$479". 100% off → FREE. Falls back to the
// %/amount label until the plan's base price has loaded.
const badgeFor = (d, planAmount) => {
  if (!d || !d.value) return '';
  if (d.type === 'percentage' && Number(d.value) === 100) return 'Included';
  if (planAmount != null && Number.isFinite(planAmount)) {
    const eff = d.type === 'percentage'
      ? planAmount * (1 - Number(d.value) / 100)
      : planAmount - Number(d.value) / 100;
    return `$${Math.round(Math.max(0, eff)).toLocaleString('en-US')}`;
  }
  return fmtDisc(d);
};

// Member view — a two-column account dashboard:
//   left  = membership summary + Manage / Cancel / Resume
//   right = the full user-management UI (same as /manage-users)
// All three states are driven by my-membership (cancel_at_period_end toggles
// Cancel ↔ Resume; is_member:false is handled by the parent → plan grid).
// membershipState:
//   'active'  → normal member header (Manage / Cancel / Resume)
//   'none'    → active affiliate without a membership → "buy to create users"
//   'expired' → active affiliate with an ended membership → "reactivate"
// When not 'active', create-user is gated behind a popup that routes to plans
// (onGetMembership). Default 'active' keeps the legacy /user-management usage
// untouched.
const MemberView = ({
  membership, onChanged, affiliateActive = false, membershipState = 'active', onGetMembership,
}) => {
  const [busy, setBusy] = useState(null); // 'manage' | 'cancel' | 'resume' | null
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false); // create-user membership gate
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // True only when a same-interval plan costs more than the current one; the
  // Upgrade button is hidden on the top plan.
  const [hasHigherPlan, setHasHigherPlan] = useState(false);

  const m = membership || {};
  const canceling = !!m.cancel_at_period_end; // active but scheduled to end
  const periodEnd = formatDate(m.current_period_end);

  // Per-cycle allowances + discounts → flatten each creation into offer tiles:
  //  • numeric `allowed` + `discount` → a capped free tier (bar, "X left").
  //  • `after_limit_discount` on a capped plan → a SEPARATE uncapped tile that
  //    kicks in once the free tier is used up (count = after_limit_used_this_cycle).
  //  • `allowed: null` + a discount → a single flat, uncapped discount tile.
  //  • no discount + no after-limit discount → not an offer, so it's dropped.
  const creations = Array.isArray(m.creations) ? m.creations : [];
  // Resolve creation plan_id → product name + base price (best-effort).
  const [planNames, setPlanNames] = useState({});
  const [planAmounts, setPlanAmounts] = useState({});
  const offerTiles = [];
  creations.forEach((c) => {
    const name = planNames[String(c.plan_id)] || `Plan ${c.plan_id}`;
    const amt = planAmounts[String(c.plan_id)];
    const capped = c.allowed !== null && c.allowed !== undefined;
    if (capped) {
      const allowed = Number(c.allowed) || 0;
      const used = Number(c.used_this_cycle) || 0;
      const exceeded = !!c.limit_exceeded || (allowed > 0 && used >= allowed);
      const remaining = c.free_remaining != null ? Number(c.free_remaining) : Math.max(0, allowed - used);
      if (c.discount) {
        offerTiles.push({
          key: `${c.plan_id}-free`,
          name,
          badge: badgeFor(c.discount, amt),
          count: `${used}/${allowed}`,
          pct: allowed > 0 ? Math.min(100, Math.round((used / allowed) * 100)) : 0,
          bar: true,
          exceeded,
          detail: exceeded ? 'Free tier used up' : `${remaining} free left`,
        });
      }
      if (c.after_limit_discount) {
        offerTiles.push({
          key: `${c.plan_id}-after`,
          name,
          note: 'After Included',
          badge: badgeFor(c.after_limit_discount, amt),
          count: '∞',
          bar: false,
          exceeded: false,
          detail: `${Number(c.after_limit_used_this_cycle) || 0} created`,
        });
      }
    } else {
      const disc = c.discount || c.after_limit_discount;
      if (disc) {
        offerTiles.push({
          key: `${c.plan_id}-flat`,
          name,
          badge: badgeFor(disc, amt),
          count: '∞',
          bar: false,
          exceeded: false,
          detail: `${Number(c.used_this_cycle) || 0} created`,
        });
      }
    }
  });
  useEffect(() => {
    if (!creations.length) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await dataFetcher(endpoints.getAllActivePlans);
        if (active && res?.success && Array.isArray(res.data)) {
          const nameMap = {};
          const amtMap = {};
          res.data.forEach((p) => {
            nameMap[String(p.id)] = p.product_name;
            amtMap[String(p.id)] = Number(p.amount);
          });
          setPlanNames(nameMap);
          setPlanAmounts(amtMap);
        }
      } catch (e) { /* names/prices are best-effort */ }
    })();
    return () => { active = false; };
  }, [creations.length]);

  // Is there a higher plan to upgrade to? (Hidden on the top plan.)
  useEffect(() => {
    if (membershipState !== 'active') { setHasHigherPlan(false); return undefined; }
    const interval = m.billing_title || 'Annual';
    const curAmount = Number(m.amount_cents) || 0;
    let active = true;
    (async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${baseUrl}/v4/membership-plans`, token ? { params: { token } } : undefined);
        if (!active) return;
        const higher = Array.isArray(data?.data) && data.data.some((p) => {
          const pr = (p.pricing || []).find((x) => x.billing_title === interval);
          return pr && Number(pr.price_cents) > curAmount;
        });
        setHasHigherPlan(!!higher);
      } catch (e) { /* keep hidden on failure */ }
    })();
    return () => { active = false; };
  }, [membershipState, m.billing_title, m.amount_cents]);

  const membershipActive = membershipState === 'active';
  const expired = membershipState === 'expired';
  const gateText = expired
    ? 'Your membership has ended. Reactivate it to create new users.'
    : 'Become a member to create and manage new users.';
  const gateCta = expired ? 'Activate membership' : 'View membership plans';
  const handleGetMembership = () => { setGateOpen(false); onGetMembership?.(); };

  const openPortal = async () => {
    setBusy('manage');
    setErr('');
    try {
      const res = await dataFetcher('v4/membership/billing-portal', { return_url: window.location.href });
      if (res?.success && res?.data?.url) {
        window.location = res.data.url; // returns here later → hook re-fetches on mount
      } else {
        setErr(res?.message || 'Could not open the billing portal.');
        setBusy(null);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not open the billing portal.');
      setBusy(null);
    }
  };

  const doCancel = async () => {
    setBusy('cancel');
    setErr('');
    setMsg('');
    try {
      const res = await dataFetcher('v4/membership/cancel'); // graceful (at period end)
      if (res?.success) {
        setMsg(res.message || 'Your membership will end at the current period.');
        setConfirmOpen(false);
        onChanged?.(true); // silent reconcile → banner + Resume appear
      } else {
        setErr(res?.message || 'Could not cancel your membership.');
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not cancel your membership.');
    } finally {
      setBusy(null);
    }
  };

  const doResume = async () => {
    setBusy('resume');
    setErr('');
    setMsg('');
    try {
      const res = await dataFetcher('v4/membership/resume');
      if (res?.success) {
        setMsg(res.message || 'Membership resumed.');
        onChanged?.(true); // silent reconcile → banner clears, Cancel returns
      } else {
        setErr(res?.message || 'Could not resume your membership.');
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not resume your membership.');
      if (e?.response?.status === 409) onChanged?.(true); // ended → becomes is_member:false
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.member}>
      {/* ---- Not a member (active affiliate): buy / reactivate banner ---- */}
      {!membershipActive && (
        <div className={`${styles.gateBanner} ${expired ? styles.gateBannerWarn : ''}`}>
          <div className={styles.gateBannerBody}>
            <span className={styles.gateBannerTitle}>
              {expired ? 'Membership required' : 'Unlock user creation'}
            </span>
            <span className={styles.gateBannerText}>{gateText}</span>
          </div>
          <button type="button" className={styles.gateBannerBtn} onClick={onGetMembership}>
            {gateCta}
          </button>
        </div>
      )}

      {/* ---- Membership header: plan info left, status + actions right ---- */}
      {membershipActive && (
      <div className={styles.memberHeader}>
        <div className={styles.memberHeaderMain}>
          <span className={styles.asideLabel}>Your membership</span>
          <div className={styles.memberHeaderPlan}>
            <span className={styles.asidePlanName}>{m.plan_name}</span>
            {m.billing_title && <span className={styles.asideCycle}>{m.billing_title}</span>}
            <span className={styles.memberHeaderPrice}>{formatMoney(m.amount_cents)}</span>
            {m.can_upgrade && hasHigherPlan && (
              <button type="button" className={styles.upgradeLink} onClick={() => setUpgradeOpen(true)}>
                Upgrade
              </button>
            )}
          </div>
          {periodEnd && (
            <div className={styles.asideRenew}>
              {canceling ? `Ends on ${periodEnd}` : `Renews on ${periodEnd}`}
            </div>
          )}
          {m.discount && (
            <div className={styles.promo}>
              <span className={styles.promoIcon} aria-hidden="true">%</span>
              <span className={styles.promoName}>
                {m.discount.name || m.discount.code || 'Promo applied'}
              </span>
              {discountValue(m.discount) && (
                <span className={styles.promoValue}>
                  {discountValue(m.discount)}
                  {discountDuration(m.discount) ? ` · ${discountDuration(m.discount)}` : ''}
                </span>
              )}
              {m.discount.code && m.discount.code !== m.discount.name && (
                <span className={styles.promoCode}>{m.discount.code}</span>
              )}
            </div>
          )}
        </div>

        <div className={styles.memberHeaderSide}>
          <span className={`${styles.statusChip} ${canceling ? styles.statusChipEnding : styles.statusChipActive}`}>
            {canceling ? 'Ending' : (m.status || 'Active')}
          </span>
          <div className={styles.memberHeaderActions}>
            <button type="button" className={`${styles.hdrBtn} ${styles.hdrManage}`} onClick={openPortal} disabled={!!busy}>
              {busy === 'manage' ? 'Opening…' : 'Manage membership'}
            </button>
            {canceling ? (
              <button type="button" className={`${styles.hdrBtn} ${styles.hdrResume}`} onClick={doResume} disabled={!!busy}>
                {busy === 'resume' ? 'Resuming…' : 'Resume membership'}
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.hdrBtn} ${styles.hdrCancel}`}
                onClick={() => setConfirmOpen(true)}
                disabled={!!busy}
              >
                Cancel membership
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ---- Usage / allowances: full-width, cardless rows with progress bars ---- */}
      {membershipActive && offerTiles.length > 0 && (
        <div className={styles.usage}>
          <span className={styles.usageLabel}>New users you can create this cycle</span>
          <div className={styles.usageList}>
            {offerTiles.map((t) => (
              <div key={t.key} className={styles.usageItem}>
                <div className={styles.usageItemHead}>
                  <span className={styles.usageName}>{t.name}</span>
                  {t.badge ? (
                    <span className={`${styles.usageBadge} ${t.badge === 'Included' ? styles.usageBadgeFree : ''}`}>
                      {t.badge}
                    </span>
                  ) : null}
                </div>
                {/* Always render the note line so the detail/count line stays on
                    the same row across items with and without a note. */}
                <span className={styles.usageNote}>{t.note || ' '}</span>
                <div className={styles.usageItemMeta}>
                  <span className={styles.usageDetail}>{t.detail}</span>
                  <span className={`${styles.usageCount} ${t.exceeded ? styles.usageCountFull : ''}`}>{t.count}</span>
                </div>
                {t.bar && (
                  <span className={styles.usageBar}>
                    <span
                      className={`${styles.usageBarFill} ${t.exceeded ? styles.usageBarFillFull : ''}`}
                      style={{ width: `${t.pct}%` }}
                    />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {membershipActive && canceling && periodEnd && (
        <div className={styles.memberBanner}>{`Your membership ends on ${periodEnd}. You keep full access until then.`}</div>
      )}
      {msg && <div className={styles.memberOk}>{msg}</div>}
      {err && <p className={styles.coError}>{err}</p>}

      <div className={styles.memberDivider} />

      {/* ---- User management (V2-structured placement) ---- */}
      <UsersPanel
        blockCreateUser={affiliateActive && !membershipActive}
        onCreateUserBlocked={() => setGateOpen(true)}
      />

      <MembershipUpgradeModal
        open={upgradeOpen}
        membership={m}
        onClose={() => setUpgradeOpen(false)}
        onUpgraded={() => { setUpgradeOpen(false); onChanged?.(true); }}
      />

      {gateOpen && (
        <div className={styles.modalOverlay} onClick={() => setGateOpen(false)}>
          <div
            className={styles.confirmCard}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalBody}>
              <h3 className={styles.confirmTitle}>
                {expired ? 'Reactivate your membership' : 'Membership required'}
              </h3>
              <p className={styles.confirmText}>
                {expired
                  ? 'Your membership has ended. Reactivate it to create new users.'
                  : 'Creating new users is a member benefit. Choose a membership plan to get started.'}
              </p>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.coSecondary} onClick={() => setGateOpen(false)}>
                  Not now
                </button>
                <button type="button" className={styles.gateModalBtn} onClick={handleGetMembership}>
                  {gateCta}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className={styles.modalOverlay} onClick={() => { if (!busy) setConfirmOpen(false); }}>
          <div
            className={styles.confirmCard}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalBody}>
              <h3 className={styles.confirmTitle}>Cancel membership?</h3>
              <p className={styles.confirmText}>
                {`Cancel your ${m.plan_name || ''} membership? You'll keep access until ${periodEnd || 'the end of the current period'}.`}
              </p>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.coSecondary}
                  onClick={() => setConfirmOpen(false)}
                  disabled={!!busy}
                >
                  Keep membership
                </button>
                <button
                  type="button"
                  className={styles.cancelConfirmBtn}
                  onClick={doCancel}
                  disabled={busy === 'cancel'}
                >
                  {busy === 'cancel' ? 'Canceling…' : 'Yes, cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberView;
